import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "./SEO";
import PageHero from "./PageHero";
import PageIntro from "./PageIntro";
import FAQSection from "./FAQSection";
import RichText from "./RichText";

const SITE_URL = "https://kinewsstudiolimited.co.uk";

const thumbs = require.context("../assets/studio-thumbs", false, /\.webp$/);
const getThumb = (n) => thumbs(`./studio-thumb${String(n).padStart(2, "0")}.webp`);

export default function ServicePage({
  path,
  seoTitle,
  seoDescription,
  serviceName,
  heroTitle,
  heroImage,
  heroPosition = "center",
  ctaImage,
  intro,
  sections,
  steps,
  stepsTitle,
  gallery,
  galleryTitle,
  faqs,
  cta,
  related,
}) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    serviceType: serviceName,
    url: `${SITE_URL}${path}`,
    areaServed: ["Rochdale", "Greater Manchester"],
    provider: {
      "@type": "ProfessionalService",
      name: "Kinews Studio Limited",
      url: SITE_URL,
      telephone: "+44 7486 160965",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Unit 2, Beechwood House, Milnrow Road",
        addressLocality: "Rochdale",
        postalCode: "OL16 2AA",
        addressCountry: "GB",
      },
    },
  };

  return (
    <main className="bg-white text-black">
      <SEO title={seoTitle} description={seoDescription} jsonLd={jsonLd} />

      <PageHero
        title={heroTitle}
        image={heroImage}
        imagePosition={heroPosition}
      />

      <PageIntro>{intro}</PageIntro>

      <section className="bg-[#fff7fb]">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-10 sm:px-10 lg:px-16 xl:px-6">
          <div className="grid gap-6 md:grid-cols-2">
            {sections.map((section) => (
              <div
                key={section.heading}
                className="rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8"
              >
                <h2 className="text-2xl font-semibold">{section.heading}</h2>
                {section.body.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-3 text-[15px] leading-relaxed text-black/70"
                  >
                    <RichText>{paragraph}</RichText>
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-20 lg:px-16 xl:px-6">
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.3em] text-pink-500/80">
              How it works
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
              {stepsTitle}
            </h2>
          </div>

          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <li
                key={step.title}
                className="relative isolate overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-2 right-5 -z-10 select-none text-[40px] font-black leading-none tracking-tighter text-black/[0.06] md:-top-3 md:right-6 md:text-[56px]"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-black/70">
                  <RichText>{step.text}</RichText>
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#fff7fb]">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-20 lg:px-16 xl:px-6">
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.3em] text-pink-500/80">
              Our work
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
              {galleryTitle}
            </h2>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3">
            {gallery.map((item) => (
              <div
                key={item.n}
                className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)]"
              >
                <img
                  src={getThumb(item.n)}
                  alt={item.alt}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95"
            >
              See More of Our Work
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <FAQSection items={faqs} />

      <section className="bg-[#fff7fb]">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-20 lg:px-16 xl:px-6">
          <div className="relative isolate overflow-hidden rounded-3xl bg-[#1c1c1c]">
            {ctaImage && (
              <div className="absolute inset-y-0 right-0 w-full md:w-3/5">
                <img
                  src={ctaImage}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[#1c1c1c]/70 md:bg-[#1c1c1c]/10" />
                <div className="absolute inset-y-0 left-0 hidden w-3/4 bg-gradient-to-r from-[#1c1c1c] via-[#1c1c1c]/70 to-transparent md:block" />
              </div>
            )}

            <div className="relative px-6 py-14 sm:px-10 md:px-14 md:py-20">
              <h2 className="max-w-xl text-3xl font-semibold leading-tight text-white md:text-4xl">
                {cta.heading}
              </h2>
              <p className="mt-4 max-w-xl text-white/75 md:text-lg">
                {cta.text}
              </p>
              <Link
                to={cta.to}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95"
              >
                {cta.label}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>

          {related?.length > 0 && (
            <div className="mt-14">
              <h2 className="text-2xl font-semibold md:text-3xl">Explore more</h2>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {related.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-black/10 bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition hover:-translate-y-0.5 hover:border-pink-300"
                  >
                    <span className="text-[15px] font-semibold leading-snug text-black">
                      {link.label}
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pink-50 text-pink-600 transition group-hover:bg-pink-500 group-hover:text-white">
                      <ArrowRight size={18} aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
